<template>
  <uploader
    :options="uploadOptions"
    @dragenter.stop="onDragEnter"
    @dragleave.stop="onDragLeave"
    @dragover.stop="onDragOver"
    @drop.stop="onDrop"
    @file-added="onFileAdded"
    @files-added="onFilesAdded"
    @file-progress="onFileProgress"
    @file-error="onFileError"
    @complete="onComplete"
    ref="fileUploadArea"
    class="upload-area"
    :auto-start="enabled"
  >
    <slot></slot>
    <section class="upload-backdrop" v-show="displayBackdrop">
      <img src="../../../images/upload.svg" />
      <span v-if="draggingMultiple">Bestanden hier uploaden</span>
      <span v-else>Bestand hier uploaden</span>
    </section>
  </uploader>
</template>

<script>
import { EventBus } from '@/eventbus/EventBus'
import ErrorMessage from '@/dialogs/ErrorMessage.vue'
import { uuidv4 } from '@/api/uuid'
import { mapStores } from 'pinia'
import { useFileStoreStore } from '@/features/filestore/FileStore.store'

export default {
  name: 'FileStoreUploader',
  props: {
    folderId: Number,
    enabled: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapStores(useFileStoreStore)
  },
  mounted() {
    this.$nextTick(() => {
      this.uploader = this.$refs.fileUploadArea.uploader
      this.uploader.assignDrop(this.$refs.fileUploadArea.$el)
    })
  },
  data() {
    return {
      displayBackdrop: false,
      draggingMultiple: true,
      duplicateNames: [],
      uploadOptions: {
        target: '/api/v2/filestore/file',
        testChunks: true,
        allowDuplicateUploads: true,
        chunkSize: 524288, // 0.5MB
        query: () => {
          return { folderId: this.folderId }
        },
        generateUniqueIdentifier: () => {
          return uuidv4()
        },
        permanentErrors: [404, 415, 422, 500, 501, 409]
      },
      foldersUploaded: false
    }
  },
  methods: {
    onDragEnter(event) {
      if (!this.enabled) return
      this.draggingMultiple = event.dataTransfer.items.length > 1
      this.displayBackdrop = true
    },
    onDragLeave() {
      if (!this.enabled) return
      this.displayBackdrop = false
    },
    onDragOver() {
      if (!this.enabled) return
      this.displayBackdrop = true
    },
    onDrop() {
      if (!this.enabled) return
      this.displayBackdrop = false
    },
    onFileAdded(file) {
      // Pause each individual file, we will validate all files later in `onFilesAdded`
      file.pause()

      if (!this.enabled) {
        file.cancel()
        return
      }

      // Skip hidden files
      if (file.name.startsWith('.')) {
        file.cancel()
        return
      }

      // Detect folder uploads
      if (file.relativePath.includes('/')) {
        this.foldersUploaded = true
      }
    },
    onFilesAdded(files) {
      if (!this.enabled && !this.rootStore.dragging) {
        this.$modal.show(
          ErrorMessage,
          {
            text:
              'Uploaden naar dit archief is niet mogelijk, u heeft onvoldoende rechten.'
          },
          {
            height: 'auto',
            width: 380,
            adaptive: true
          }
        )
        return
      }

      const fileNames = this.fileStoreStore
        .getFilesForFolder(this.folderId)
        .map((item) => item.name)

      files.forEach((file) => {
        if (fileNames.includes(file.name)) {
          this.duplicateNames.push(file.name)
        }
      })

      if (this.duplicateNames.length === 0) {
        files.forEach((file) => file.resume())
      } else {
        let message, action

        if (this.duplicateNames.length === 1) {
          message = `Er bestaat al een bestand met de naam <strong>${this.duplicateNames[0]}</strong> in deze map. Wilt u het bestand vervangen?`
          action = 'bestand vervangen'
        } else {
          message = `<strong>${this.duplicateNames.length} van de ${files.length}</strong> bestanden bestaan al in deze map. Als u doorgaat worden deze overschreven.`
          action = 'overschrijven'
        }

        this.$modal.show('dialog', {
          text: message,
          buttons: [
            {
              title: 'Annuleren',
              class: 'pill-button secondary',
              handler: () => {
                // Cancel all uploads
                files.forEach((file) => file.cancel())
                this.$modal.hide('dialog')
              }
            },
            {
              title: action,
              class: 'pill-button',
              handler: () => {
                // Start uploads for all files
                // NOTE: we are doing this because `uploader.upload()` does not work as documented
                files.forEach((file) => file.resume())
                this.$modal.hide('dialog')
              }
            }
          ]
        })

        this.duplicateNames = []
      }
    },
    onFileProgress() {
      this.$emit('progress', Math.floor(this.uploader.progress() * 100))
    },
    onFileError(rootFile, file, message) {
      this.foldersUploaded = false
      EventBus.emit('fileStoreErrorDialog', {
        title: `Uploaden van bestand ${file.name} is mislukt`,
        message: JSON.parse(message).message
      })
    },
    onComplete() {
      this.$emit('complete', { foldersUploaded: this.foldersUploaded })
      this.foldersUploaded = false
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-backdrop {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  color: var(--sub-color);
  font-size: 18px;
  pointer-events: none;

  svg {
    margin-bottom: 10px;

    path {
      stroke: var(--sub-color);
    }
  }
}

.upload-area {
  position: relative;
}
</style>
